import {
	Button,
	Checkbox,
	Flex,
	Text,
	TextField,
	Icon,
	useModal,
} from "@capioweb/ui-components/src"
import React from "react"
import { SharedFormProps } from "../../../../@types/CapioWeb/core"
import { useForm } from "react-hook-form"
import { postAccountForm } from "../../api/postAccountForm"
import { InfoModal } from "../../../../pages/MyPagesUserRegistrationPage/InfoModal"
import { LocalesContext } from "../../../../context"
import { getAntiforgeryToken } from '../../../../utils/antiforgery'
import { StyledButtonContent, StyledIcon } from '../../../MyPagesUserLifestyleFormBlock/LifestyleForm'

const ManageAccount: React.FC<SharedFormProps> = (props) => {

	const { translate, translationKeys } = React.useContext(LocalesContext)
	const [loading, setLoading] = React.useState<boolean>(false)


	// Register form
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm({
		mode: "onChange",
	})

	// Submit handler
	const onSubmit = async (data) => {

		setLoading(true)

		const { action } = props
		const antiForgeryToken = await getAntiforgeryToken()
		try {
			const result = await postAccountForm(
				action,
				antiForgeryToken,
				data,
				props.method
			)

			if (result.success) {
				setFeedbackMsg({
					success: true,
					message: translate(
						translationKeys.manageAccountFeedbackMessageSuccess
					),
				})
			} else {
				setFeedbackMsg({
					success: false,
					message: result.data?.message ?? "Något gick fel",
				})
			}
		} catch (e) {
			console.error("---Catch", e, "--- error")
		}

		setLoading(false)

	}

	// Get props for submit button
	const submitButtonProps = props.fields.find(
		(field) => field.type === "submit"
	)

	// Feedback message
	const [feedbackMsg, setFeedbackMsg] =
		React.useState<{
			success: boolean
			message: string
		}>(null)

	// Modal hook
	const { addModal } = useModal()

	return (
		<Flex
			as="form"
			action={props.action}
			encType="application/x-www-form-urlencoded"
			method={props.method}
			onSubmit={handleSubmit(onSubmit)}
			flexDirection="column"
			maxw={58}
		>
			{props.fields.map((field) => {
				const isInputField = !["submit"].includes(field.type)
				const requiredFields = [
					"name",
					"phonenumber",
					"street",
					"postalcode",
					"city",
					"terms",
				]

				if (field && field.type && isInputField) {
					const { type, value, required, ...rest } = field
					const { ref, ...registerProps } = register(field.name, {
						required: {
							value:
								requiredFields.includes(field.name) && !field.disabled
									? true
									: false,
							message:
								props.requiredMessage !== ""
									? props.requiredMessage
									: translate(translationKeys.requiredFieldMessage),
						},
						...(field.validators?.length > 0 && {
							pattern: {
								value: new RegExp(field.validators[0].pattern),
								message: field.validators[0].message,
							},
						}),
					})
					const fieldProps = {
						...registerProps,
						...rest,
						type,
						key: field.id,
						error: errors[field.name],
						...(field.type !== "hidden" &&
							errors[field.name] && {
							helperText: errors[field.name].message,
						}),
					}

					if (["text", "email", "tel"].includes(type)) {
						return (
							<TextField inputRef={ref} defaultValue={value} {...fieldProps} />
						)
					} else {
						return {
							checkbox: (
								<Flex key={field.id} mb={2} justifyContent="space-between">
									<Checkbox
										inputRef={ref}
										value={
											field.items?.length > 0
												? field.items[0].value
												: field.label
										}
										defaultChecked={
											field.items?.length > 0 && field.items[0].checked
										}
										{...fieldProps}
									/>
									{props.modals?.find((m) => m.id === field.id) && (
										<Flex
											as="button"
											aria-label={`${translate(translationKeys.openModalFor)} ${field.label
												}`}
											flexShrink={0}
											onClick={(e) => {
												e.preventDefault()
												addModal(
													<InfoModal
														lastFocusedElement={e.target}
														{...props.modals.find((m) => m.id === field.id)}
													/>
												)
											}}
										>
											<Icon
												icon="info-circle"
												size={3}
											// style={{ pointerEvents: "none" }}
											/>
										</Flex>
									)}
								</Flex>
							),
							hidden: (
								<input
									ref={ref}
									readOnly
									key={field.value}
									value={field.value}
									{...fieldProps}
								/>
							),
						}[type]
					}
				}
			})}
			{submitButtonProps && (
				<Flex flexDirection="column" alignItems="flex-start">
					<Button mt={3} style={{ position: 'relative' }} type={loading ? 'button' : "submit"}>
						<StyledIcon aria-hidden loading={loading}>
							<Icon icon="ellipse" color='white' />
						</StyledIcon>
						<StyledButtonContent loading={loading}>{translate(translationKeys.save)}</StyledButtonContent>
					</Button>
					{/* Feedback message - used if we don't redirect (errors & success) */}
					<Flex
						h={feedbackMsg ? "auto" : "0"}
						overflow="hidden"
						w={1}
						mt={1.5}
						role="region"
						aria-live="polite"
					>
						<Text
							as="span"
							textColor={feedbackMsg?.success ? "primary" : "alert"}
						>
							{feedbackMsg?.message}
						</Text>
					</Flex>
				</Flex>
			)}
		</Flex>
	)
}

export { ManageAccount }
